import type { ReactNode } from "react";

type StatusBannerTone = "info" | "success" | "warning" | "pending" | "danger";

interface StatusBannerProps {
  action?: ReactNode;
  description?: string;
  label: string;
  /** `null` keeps the region mounted with nothing to say. */
  title: string | null;
  tone?: StatusBannerTone;
}

/**
 * The live region is the `<aside>`, and it outlives the notice inside it.
 *
 * A screen reader announces changes *within* a live region it is already
 * watching; a region that arrives already populated is usually not announced
 * at all. Rendering nothing until there was something to say therefore meant
 * an installed reader could miss the update prompt entirely -- the one notice
 * that has an action attached to it. The strip is now a child that comes and
 * goes while the region stays put; empty, it is taken out of flow so it costs
 * neither a box nor a flex gap.
 */
export function StatusBanner({
  action,
  description,
  label,
  title,
  tone = "info",
}: StatusBannerProps) {
  return (
    <aside
      aria-label={label}
      aria-live={tone === "danger" ? "assertive" : "polite"}
      className="system-banner-live"
    >
      {title === null ? null : (
        <div className={`system-banner system-banner--${tone}`}>
          <div className="system-banner__message">
            <strong>{title}</strong>
            {description ? <p>{description}</p> : null}
          </div>
          {action}
        </div>
      )}
    </aside>
  );
}
